import React, { useEffect, useState } from "react"
import Button from "@mui/material/Button"
import LinearProgress from "@mui/material/LinearProgress"
import Pagination from "@mui/material/Pagination"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import { useSnackbar } from "notistack"
import { UseConfirmDialog } from "components/customHooks"
import { getReportedPosts, ignoreReport, removeReportedPost, banAuthorByPost } from "services/community"
import { buildCommunityPostUrl } from "utils/platformUrl"
import { StyledCommunityReports } from "./Community.style"

const ReportedPosts = () => {
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Confirm moderation action",
    description: "Are you sure you want to continue?",
    agreeText: "Proceed",
    disagreeText: "Cancel",
  })

  const load = async (p = 1) => {
    setLoading(true)
    setError(null)
    try {
      const res = await getReportedPosts(p, pageSize)
      setRows(Array.isArray(res?.data) ? res.data : [])
      setTotal(Number(res?.total) || 0)
      setPage(Number(res?.page) || p)
    } catch (e) {
      setError("Failed to load reported posts")
    } finally {
      setLoading(false)
    }
  }

  const performAction = async (action, successMessage, errorMessage) => {
    setActionLoading(true)
    try {
      await action()
      enqueueSnackbar(successMessage, { variant: "success" })
      await load(page)
    } catch (e) {
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setActionLoading(false)
    }
  }

  useEffect(() => {
    load(1)
  }, [])

  const onIgnore = async (reportId) => {
    if (!reportId) return
    await performAction(
      () => ignoreReport(reportId),
      "Post report ignored",
      "Failed to ignore post report"
    )
  }

  const onRemove = async (postId) => {
    if (!postId) return
    await performAction(
      () => removeReportedPost(postId),
      "Reported post removed",
      "Failed to remove reported post"
    )
  }

  const onBan = async (postId) => {
    if (!postId) return
    await performAction(
      () => banAuthorByPost(postId),
      "Post author banned",
      "Failed to ban post author"
    )
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <StyledCommunityReports>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Reported Posts</h1>
          <p className="page-subtitle">
            Review flagged community posts and apply moderation actions.
          </p>
        </div>
        <div className="actions"></div>
      </PageHeading>
      <div className="page-surface">
        <div className="status-row">
          <div className="status-info">Total reports: {total}</div>
          {error && <div className="status-error">{error}</div>}
        </div>
        <div className="table-shell">
          <TableContainer component={Paper}>
            {loading && <LinearProgress />}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reported At</TableCell>
                  <TableCell>Post</TableCell>
                  <TableCell>Media / Links</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Reporter</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell className="empty-row" colSpan={7}>
                      No reported posts found.
                    </TableCell>
                  </TableRow>
                )}
                {(rows || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="content-cell">
                      <div className="content-title">{r.post?.title || "(No title)"}</div>
                      <div className="content-subtext">{r.post?.content || "-"}</div>
                      {r.post?.id && (
                        <a
                          className="report-link"
                          href={buildCommunityPostUrl(r.post.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Post
                          <OpenInNewRoundedIcon fontSize="inherit" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      {Array.isArray(r.post?.mediaUrls) && r.post.mediaUrls.length > 0 ? (
                        <div className="list-links">
                          {r.post.mediaUrls.slice(0, 3).map((u, idx) => (
                            <a
                              key={idx}
                              className="report-link"
                              href={u}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {u}
                            </a>
                          ))}
                          {r.post.mediaUrls.length > 3 && (
                            <div className="muted">
                              +{r.post.mediaUrls.length - 3} more
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="muted">No media</div>
                      )}
                      {r.post?.linkUrl && (
                        <div className="list-links">
                          <a
                            className="report-link"
                            href={r.post.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Link: {r.post.linkUrl}
                          </a>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.post?.author ? (
                        <div>
                          <div>{r.post.author.name}</div>
                          <div className="muted">{r.post.author.username}</div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{r.reporter ? r.reporter.name : "-"}</TableCell>
                    <TableCell>
                      <div className="reason-main">{r.reason || "-"}</div>
                      {r.details && <div className="muted">{r.details}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="action-group">
                        <Button
                          className="action-btn remove"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            openConfirmDialog(
                              () => onRemove(r.post?.id),
                              "Remove this post from the community feed?"
                            )
                          }}
                          disabled={actionLoading || !r.post?.id}
                        >
                          Remove Post
                        </Button>
                        <Button
                          className="action-btn ban"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            openConfirmDialog(
                              () => onBan(r.post?.id),
                              "Ban this post author from the community?"
                            )
                          }}
                          disabled={actionLoading || !r.post?.id}
                        >
                          Ban User
                        </Button>
                        <Button
                          className="action-btn ignore"
                          variant="outlined"
                          size="small"
                          onClick={() => onIgnore(r.id)}
                          disabled={actionLoading}
                        >
                          Ignore
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        {totalPages > 1 && (
          <div className="pagination-wrap">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => {
                load(value)
              }}
              color="primary"
              shape="rounded"
              size="small"
            />
          </div>
        )}
      </div>
      {ConfirmDialog}
    </StyledCommunityReports>
  )
}

export default ReportedPosts


